import { createContext, useContext, useState, useCallback, useEffect, useMemo, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient, initCsrf } from '@/lib/api';
import { API_ROUTES } from '@/config/constants';
import type { User, Membership, Gad } from '@/types/common';

interface AuthMeResponse {
  user: User;
  memberships: Membership[];
  active_membership_id: number;
}

interface SwitchGadResponse {
  active_gad: Gad;
  active_membership: Membership;
}

interface AuthState {
  user: User | null;
  memberships: Membership[];
  activeMembershipId: number | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isSwitchingGad: boolean;
}

interface AuthContextType extends Omit<AuthState, 'isSwitchingGad'> {
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  switchGad: (gadId: number) => Promise<void>;
  isSwitchingGad: boolean;
  activeGad: Gad | null;
  activeMembership: Membership | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const queryClient = useQueryClient();
  const [state, setState] = useState<AuthState>({
    user: null,
    memberships: [],
    activeMembershipId: null,
    isAuthenticated: false,
    isLoading: true,
    isSwitchingGad: false,
  });

  // Computed values
  const activeMembership = useMemo(() => {
    return state.memberships.find((m) => m.id === state.activeMembershipId) ?? null;
  }, [state.memberships, state.activeMembershipId]);

  const activeGad = useMemo(() => {
    return activeMembership?.gad ?? null;
  }, [activeMembership]);

  /**
   * Check if user is authenticated on mount
   * Uses httpOnly cookie for session
   */
  const checkAuth = useCallback(async () => {
    try {
      setState((prev) => ({ ...prev, isLoading: true }));
      const response = await apiClient.get<AuthMeResponse>(API_ROUTES.AUTH.ME);
      setState({
        user: response.user,
        memberships: response.memberships,
        activeMembershipId: response.active_membership_id,
        isAuthenticated: true,
        isLoading: false,
        isSwitchingGad: false,
      });
    } catch {
      setState({
        user: null,
        memberships: [],
        activeMembershipId: null,
        isAuthenticated: false,
        isLoading: false,
        isSwitchingGad: false,
      });
    }
  }, []);

  /**
   * Login with email and password
   * Backend sets httpOnly cookie
   */
  const login = useCallback(async (email: string, password: string) => {
    const response = await apiClient.post<AuthMeResponse>(API_ROUTES.AUTH.LOGIN, {
      email,
      password,
    });
    setState({
      user: response.user,
      memberships: response.memberships,
      activeMembershipId: response.active_membership_id,
      isAuthenticated: true,
      isLoading: false,
      isSwitchingGad: false,
    });
  }, []);

  /**
   * Logout - clears httpOnly cookie on backend
   */
  const logout = useCallback(async () => {
    try {
      await apiClient.post(API_ROUTES.AUTH.LOGOUT);
    } finally {
      setState({
        user: null,
        memberships: [],
        activeMembershipId: null,
        isAuthenticated: false,
        isLoading: false,
        isSwitchingGad: false,
      });
      // Clear all cached queries
      queryClient.clear();
      // Refresh CSRF token for next login
      await initCsrf();
    }
  }, [queryClient]);

  /**
   * Switch active GAD
   * Updates the active membership and reloads all data
   */
  const switchGad = useCallback(
    async (gadId: number) => {
      try {
        setState((prev) => ({ ...prev, isSwitchingGad: true }));
        const response = await apiClient.post<SwitchGadResponse>(API_ROUTES.GADS.ACTIVE, {
          gad_id: gadId,
        });
        setState((prev) => ({
          ...prev,
          activeMembershipId: response.active_membership.id,
          isSwitchingGad: false,
        }));
        // Invalidate all queries to reload data for new GAD
        await queryClient.invalidateQueries();
      } catch (error) {
        setState((prev) => ({ ...prev, isSwitchingGad: false }));
        throw error;
      }
    },
    [queryClient]
  );

  // Initialize CSRF and check auth on mount
  useEffect(() => {
    const initialize = async () => {
      // First, fetch CSRF token
      await initCsrf();
      // Then check if user is authenticated
      await checkAuth();
    };
    void initialize();
  }, [checkAuth]);

  const contextValue = useMemo(
    () => ({
      user: state.user,
      memberships: state.memberships,
      activeMembershipId: state.activeMembershipId,
      isAuthenticated: state.isAuthenticated,
      isLoading: state.isLoading,
      isSwitchingGad: state.isSwitchingGad,
      activeGad,
      activeMembership,
      login,
      logout,
      checkAuth,
      switchGad,
    }),
    [state, activeGad, activeMembership, login, logout, checkAuth, switchGad]
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}

/**
 * Hook to access auth context
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
